import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";

export class CatchFoodGameScene extends BasicScene {
  // Game objects
  private player!: Phaser.Physics.Arcade.Sprite;
  private foods!: Phaser.Physics.Arcade.Group;
  private obstacles!: Phaser.Physics.Arcade.Group;
  private background!: Phaser.GameObjects.Image; // Background image

  // Game state
  private score: number = 0;
  private missedFood: number = 0;
  private hitObstacles: number = 0;
  private gameActive: boolean = true;
  private isDragging: boolean = false;
  private gameTime: number = 0; // Track game time for acceleration
  private deathCount: number = 0; // Track number of deaths

  // UI elements
  private scoreText!: Phaser.GameObjects.Text;
  private missedFoodText!: Phaser.GameObjects.Text;
  private hitObstaclesText!: Phaser.GameObjects.Text;

  // Game settings
  private readonly FOOD_SPEED: number = 250;
  private readonly OBSTACLE_SPEED: number = 250;
  private readonly MAX_MISSED_FOOD: number = 8;
  private readonly MAX_HIT_OBSTACLES: number = 3;
  private readonly SPEED_INCREASE_RATE: number = 0.08; // Rate of speed increase per second
  private readonly SPAWN_RATE_INCREASE: number = 0.03; // Rate of spawn frequency increase per second
  private foodSpawnTimer!: Phaser.Time.TimerEvent;
  private obstacleSpawnTimer!: Phaser.Time.TimerEvent;
  // New: Single spawn timer for both foods and obstacles
  private itemSpawnTimer!: Phaser.Time.TimerEvent;

  // Keyboard controls
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;

  constructor() {
    super("CatchFoodGameScene");
  }

  create(): void {
    // this.soundManager.playMusic(this.keys.assets.sounds.music.home);

    // Disable physics debug visualization for all objects
    this.physics.world.debugGraphic.setVisible(false);
    this.physics.world.debugGraphic.setActive(false);

    const { width, height } = this.cameras.main;

    // Add background image
    this.background = this.add.image(
      width / 2,
      height / 2,
      this.atlas,
      this.keys.assets.sprites.backgrounds.miniGame.background,
    );
    this.background.setDisplaySize(width, height);
    // Send background to back
    this.background.setDepth(-1);

    // Create player (cat) at the bottom center with increased size
    this.player = this.physics.add.sprite(
      width / 2,
      height - 150,
      this.atlas,
      this.keys.assets.sprites
        .pets[
          this.storage.pet().type as keyof typeof this.keys.assets.sprites.pets
        ].normal,
    );
    this.player.setScale(1); // Increased from 0.5 to 0.8
    this.player.setCollideWorldBounds(true);

    // Enable drag for the player
    this.player.setInteractive();
    this.input.setDraggable(this.player);

    // Add drag event listeners
    this.player.on("dragstart", () => {
      this.isDragging = true;
    });

    this.player.on("drag", (pointer: Phaser.Input.Pointer, dragX: number) => {
      if (!this.gameActive) return;

      // Only move horizontally, keep vertical position
      this.player.x = dragX;

      // Ensure player stays within bounds
      const playerWidth = this.player.displayWidth / 2;
      if (this.player.x < playerWidth) {
        this.player.x = playerWidth;
      }
      if (this.player.x > width - playerWidth) {
        this.player.x = width - playerWidth;
      }
    });

    this.player.on("dragend", () => {
      this.isDragging = false;
    });

    // Create food and obstacle groups
    this.foods = this.physics.add.group();

    this.obstacles = this.physics.add.group();

    // Create UI elements
    this.scoreText = this.add.text(
      this.cameras.main.width / 2,
      34,
      `${this.keys.strings.catchFoodScene.score}: 0`,
      {
        fontSize: "40px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
        padding: { x: 8, y: 16 },
      },
    );
    this.scoreText.setOrigin(0.5, 0); // Center horizontally, align top

    this.missedFoodText = this.add.text(
      19,
      98,
      `${this.keys.strings.catchFoodScene.miss}: 0/${this.MAX_MISSED_FOOD}`,
      {
        fontSize: "40px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
        padding: { x: 8, y: 16 },
      },
    );
    this.missedFoodText.setOrigin(0, 0); // Left-align horizontally, align top

    this.hitObstaclesText = this.add.text(
      this.cameras.main.width - 16,
      98,
      `${this.keys.strings.catchFoodScene.obstacleCatched}: 0/${this.MAX_HIT_OBSTACLES}`,
      {
        fontSize: "40px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
        padding: { x: 8, y: 16 },
      },
    );
    this.hitObstaclesText.setOrigin(1, 0); // Right-align horizontally, align top

    // Set up collisions
    this.physics.add.overlap(
      this.player,
      this.foods,
      this.collectFood,
      undefined,
      this,
    );

    this.physics.add.overlap(
      this.player,
      this.obstacles,
      this.hitObstacle,
      undefined,
      this,
    );

    // Start spawning items with single timer
    this.itemSpawnTimer = this.time.addEvent({
      delay: 1000,
      callback: this.spawnItem,
      callbackScope: this,
      loop: true,
    });

    // Initialize game state
    this.initGameState();

    // Initialize cursor keys for keyboard input
    this.cursors = this.input.keyboard?.createCursorKeys() ||
      {} as Phaser.Types.Input.Keyboard.CursorKeys;
  }

  override update(time: number, delta: number): void {
    if (!this.gameActive) return;

    // Handle keyboard input for player movement
    if (this.cursors && !this.isDragging) {
      const { width } = this.cameras.main;
      const playerWidth = this.player.displayWidth / 2;
      const moveSpeed = 15; // Pixels per frame

      if (this.cursors.left?.isDown) {
        this.player.x -= moveSpeed;
        // Ensure player stays within bounds
        if (this.player.x < playerWidth) {
          this.player.x = playerWidth;
        }
      }

      if (this.cursors.right?.isDown) {
        this.player.x += moveSpeed;
        // Ensure player stays within bounds
        if (this.player.x > width - playerWidth) {
          this.player.x = width - playerWidth;
        }
      }
    }

    // Update game time for acceleration
    this.gameTime += delta;

    // Apply acceleration to existing items
    this.applyAcceleration();

    // Update spawn rates
    this.updateSpawnRates();

    // Update rotation for foods
    this.foods.children.entries.forEach((food: any) => {
      const rotationSpeed = food.getData("rotationSpeed");
      if (rotationSpeed) {
        food.angle += rotationSpeed * (delta / 1000);
      }
    });

    // Update rotation for obstacles
    this.obstacles.children.entries.forEach((obstacle: any) => {
      const rotationSpeed = obstacle.getData("rotationSpeed");
      if (rotationSpeed) {
        obstacle.angle += rotationSpeed * (delta / 1000);
      }
    });

    // Check if items have fallen off the bottom of the screen
    this.foods.children.entries.forEach((food: any) => {
      if (food.y > this.cameras.main.height) {
        this.missedFood++;
        this.missedFoodText.setText(
          `${this.keys.strings.catchFoodScene.miss}: ${this.missedFood}/${this.MAX_MISSED_FOOD}`,
        );
        food.destroy();

        if (this.missedFood >= this.MAX_MISSED_FOOD) {
          this.endGame(false);
        }
      }
    });

    this.obstacles.children.entries.forEach((obstacle: any) => {
      if (obstacle.y > this.cameras.main.height) {
        obstacle.destroy();
      }
    });
  }

  private initGameState(resetDeathCount: boolean = true): void {
    // Reset game state
    this.score = 0;
    this.missedFood = 0;
    this.hitObstacles = 0;
    this.gameActive = true;
    this.isDragging = false;
    this.gameTime = 0; // Reset game time

    // Reset death count only when explicitly requested (new game)
    if (resetDeathCount) {
      this.deathCount = 0;
    }

    // Reset UI
    this.scoreText.setText(`${this.keys.strings.catchFoodScene.score}: 0`);
    this.missedFoodText.setText(
      `${this.keys.strings.catchFoodScene.miss}: 0/${this.MAX_MISSED_FOOD}`,
    );
    this.hitObstaclesText.setText(
      `${this.keys.strings.catchFoodScene.obstacleCatched}: 0/${this.MAX_HIT_OBSTACLES}`,
    );

    // Clear existing items
    this.foods.clear(true, true);
    this.obstacles.clear(true, true);

    // Reset timers
    if (this.itemSpawnTimer) {
      this.itemSpawnTimer.destroy();
    }

    // Restart timer
    this.itemSpawnTimer = this.time.addEvent({
      delay: 1000,
      callback: this.spawnItem,
      callbackScope: this,
      loop: true,
    });
  }

  private updateSpawnRates(): void {
    // Calculate spawn rate multiplier based on game time
    const spawnRateMultiplier = 1 +
      (this.gameTime / 1000) * this.SPAWN_RATE_INCREASE;

    // Update item spawn timer
    const newItemDelay = Math.max(200, 1000 / spawnRateMultiplier); // Minimum 200ms delay
    if (Math.abs(this.itemSpawnTimer.delay - newItemDelay) > 50) { // Only update if change is significant
      // Destroy and recreate timer with new delay
      this.itemSpawnTimer.destroy();
      this.itemSpawnTimer = this.time.addEvent({
        delay: newItemDelay,
        callback: this.spawnItem,
        callbackScope: this,
        loop: true,
      });
    }
  }

  private applyAcceleration(): void {
    // Calculate speed multiplier based on game time
    const speedMultiplier = 1 +
      (this.gameTime / 1000) * this.SPEED_INCREASE_RATE;

    // Apply acceleration to foods
    this.foods.children.entries.forEach((food: any) => {
      const baseVelocity = this.FOOD_SPEED;
      food.setVelocityY(baseVelocity * speedMultiplier);
    });

    // Apply acceleration to obstacles
    this.obstacles.children.entries.forEach((obstacle: any) => {
      const baseVelocity = this.OBSTACLE_SPEED;
      obstacle.setVelocityY(baseVelocity * speedMultiplier);
    });
  }

  // New: Combined spawn function for both foods and obstacles
  private spawnItem(): void {
    if (!this.gameActive) return;

    const x = Phaser.Math.Between(50, this.cameras.main.width - 50);

    // Randomly decide whether to spawn food or obstacle (50/50 chance)
    if (Phaser.Math.RND.frac() < 0.5) {
      // Spawn food
      this.spawnFoodAtPosition(x);
    } else {
      // Spawn obstacle
      this.spawnObstacleAtPosition(x);
    }
  }

  // Modified: Spawn food at specific position
  private spawnFoodAtPosition(x: number): void {
    // Select a random food type from the available options
    const foodTypes = [
      this.keys.assets.sprites.food.carrot,
      this.keys.assets.sprites.food.chicken,
      this.keys.assets.sprites.food.appleGreen,
      this.keys.assets.sprites.food.appleRed,
    ];
    const randomFood = Phaser.Math.RND.pick(foodTypes);

    const food = this.foods.create(x, -50, this.atlas, randomFood);
    food.setScale(1); // Increased from 0.4 to 0.6

    // Set initial velocity with acceleration
    const speedMultiplier = 1 +
      (this.gameTime / 1000) * this.SPEED_INCREASE_RATE;
    food.setVelocityY(this.FOOD_SPEED * speedMultiplier);

    // Add random rotation properties
    const rotationSpeed = Phaser.Math.RND.between(50, 150) *
      (Phaser.Math.RND.frac() > 0.5 ? 1 : -1);
    food.setData("rotationSpeed", rotationSpeed);
  }

  // Modified: Spawn obstacle at specific position
  private spawnObstacleAtPosition(x: number): void {
    // Select a random obstacle type from the available options
    const obstacleTypes = [
      this.keys.assets.sprites.items.soap,
      this.keys.assets.sprites.items.phone,
      this.keys.assets.sprites.items.ball,
    ];
    const randomObstacle = Phaser.Math.RND.pick(obstacleTypes);

    const obstacle = this.obstacles.create(x, -50, this.atlas, randomObstacle);

    obstacle.setScale(1.4); // Increased from 0.3 to 0.5

    // Reduce collision bounds for obstacles (make them smaller)
    obstacle.body.setSize(obstacle.width * 0.5, obstacle.height * 0.5);

    // Set initial velocity with acceleration
    const speedMultiplier = 1 +
      (this.gameTime / 1000) * this.SPEED_INCREASE_RATE;
    obstacle.setVelocityY(this.OBSTACLE_SPEED * speedMultiplier);

    // Add random rotation properties
    const rotationSpeed = Phaser.Math.RND.between(50, 150) *
      (Phaser.Math.RND.frac() > 0.5 ? 1 : -1);
    obstacle.setData("rotationSpeed", rotationSpeed);
  }

  private collectFood(player: any, food: any): void {
    if (!this.gameActive) return;

    food.destroy();
    this.score += 1;
    this.scoreText.setText(
      `${this.keys.strings.catchFoodScene.score}: ${this.score}`,
    );

    if (this.settings.getSetting("isVibrationEnabled")) {
      this.telegram.twaSDK.hapticFeedback.notificationOccurred("success");
    }

    // Play success sound
    this.soundManager.playSound(this.keys.assets.sounds.miniGame.success);
  }

  private hitObstacle(player: any, obstacle: any): void {
    if (!this.gameActive) return;

    obstacle.destroy();
    this.hitObstacles++;
    this.hitObstaclesText.setText(
      `${this.keys.strings.catchFoodScene.obstacleCatched}: ${this.hitObstacles}/${this.MAX_HIT_OBSTACLES}`,
    );

    if (this.settings.getSetting("isVibrationEnabled")) {
      this.telegram.twaSDK.hapticFeedback.notificationOccurred("error");
    }

    // Play hit sound
    this.soundManager.playSound(this.keys.assets.sounds.miniGame.hit);

    if (this.hitObstacles >= this.MAX_HIT_OBSTACLES) {
      this.endGame(false);
    }
  }

  private async endGame(isWin: boolean): Promise<void> {
    this.gameActive = false;

    // Stop all items
    this.foods.children.entries.forEach((food: any) => {
      food.setVelocity(0, 0);
    });

    this.obstacles.children.entries.forEach((obstacle: any) => {
      obstacle.setVelocity(0, 0);
    });

    // Increment death count
    this.deathCount++;

    // First death - show continue overlay
    if (this.deathCount === 1) {
      this.showContinueGameOverlay();
    } else {
      // Second or subsequent death - show game over overlay with API call
      await this.showGameOverWithApi();
    }
  }

  private showGameOverOverlay(
    isWin: boolean,
    record?: number,
    coinsGained?: number,
  ): void {
    // Show game over overlay
    this.scene.pause();
    const overlayKey = this.keys.scenes.overlays.gameOverOverlay;

    this.scene.launch(overlayKey, {
      fromScene: this.scene.key,
      showCloseButton: false,
      isWin: isWin,
      score: this.score,
      record: record,
      coinsGained,
      onRestart: () => {
        // Close the overlay and restart the game
        this.scene.stop(overlayKey);
        this.scene.resume();
        this.restartGame();
      },
      onBack: () => {
        // Close the overlay and go back to home scene
        this.scene.stop(overlayKey);
        this.scene.resume();
        this.transition.toScene(
          this.keys.scenes.petHomeScene,
          this.keys.strings.sceneTransitions.goingBack,
        );
      },
    } as any);

    this.scene.bringToTop(overlayKey);
  }

  private showContinueGameOverlay(): void {
    // Show continue game overlay
    this.scene.pause();
    const overlayKey = "ContinueGameOverlay";

    this.scene.launch(overlayKey, {
      fromScene: this.scene.key,
      showCloseButton: false,
      score: this.score,
      onContinue: () => {
        // Close the overlay and continue the game
        this.scene.stop(overlayKey);
        this.scene.resume();
        this.continueGame();
      },
      onResults: () => {
        // Close the overlay and go to results (game over)
        this.scene.stop(overlayKey);
        this.scene.resume();
        this.showGameOverWithApi();
      },
    } as any);

    this.scene.bringToTop(overlayKey);
  }

  private async showGameOverWithApi(): Promise<void> {
    // Show loading overlay while making API call
    this.overlay.show("LoadingOverlay", false);

    try {
      // Call mini-game API endpoint
      const resp = await this.api.miniGame.postScore(this.score);

      // Close loading overlay
      this.overlay.close("LoadingOverlay");

      if (resp) {
        // Update pet data in registry
        if (resp.data) {
          this.registry.set("pet", resp.data);
        }

        // Show game over overlay with record info
        this.showGameOverOverlay(
          false,
          resp.data?.record,
          resp.data?.coinsGained,
        );
      } else {
        console.error("Error posting mini-game score:");
        // Show game over overlay without record info
        this.showGameOverOverlay(false);
      }
    } catch (error) {
      console.error("Error posting mini-game score:", error);
      // Close loading overlay
      this.overlay.close("LoadingOverlay");
      // Show game over overlay without record info
      this.showGameOverOverlay(false);
    }
  }

  private continueGame(): void {
    // Reset missed food and hit obstacles counters
    this.missedFood = 0;
    this.hitObstacles = 0;

    // Reduce game speed slightly (90% of current speed)
    this.gameTime = Math.round(this.gameTime * 0.6); // Reduce accumulated time

    // Reset UI
    this.missedFoodText.setText(
      `${this.keys.strings.catchFoodScene.miss}: 0/${this.MAX_MISSED_FOOD}`,
    );
    this.hitObstaclesText.setText(
      `${this.keys.strings.catchFoodScene.obstacleCatched}: 0/${this.MAX_HIT_OBSTACLES}`,
    );

    // Resume game activity
    this.gameActive = true;
  }

  private restartGame(): void {
    // Reinitialize game state completely (new game)
    this.initGameState(true);
  }
}
